import type { Brand, Category, StockLevel, StockState, StockSummary, StockValueByCurrency, Warehouse } from '@erp/shared';
import { AlertTriangle, ArrowUpDown, ChevronLeft, ChevronRight, CircleOff, Columns3, FileSpreadsheet, Package, PackageX, TrendingDown, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Card, Skeleton,
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/shadcn/index.js';
import {
  Button,
  Combobox,
  type ComboboxOption,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  type DataTableFooterRow,
  EmptyState,
} from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { cn } from '../../lib/cn.js';
import { localDateStamp } from '../../lib/csv.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { loadViewPrefs, saveViewPrefs, viewPrefsKey } from '../../lib/viewPrefs.js';
import { downloadXlsx, type XlsxValue } from '../../lib/xlsxExport.js';

import { AdjustStockDialog } from './AdjustStockDialog.js';

// --- persisted UI state (per tenant AND user, localStorage) ----------------
// Same convention as the Items page: a shop looks at the same slice of stock
// every morning, and re-picking the warehouse on every visit is friction. Per
// user because the office machine is shared — see lib/viewPrefs.
const colsKey = (t: string | null, u: string | null): string => viewPrefsKey('stock-cols', t, u);
const filtersKey = (t: string | null, u: string | null): string => viewPrefsKey('stock-filters', t, u);
/** The tenant-only keys these used before being scoped to a user — read once. */
const legacyColsKey = (t: string | null): string => `erp-stock-cols:${t ?? 'x'}`;
const legacyFiltersKey = (t: string | null): string => `erp-stock-filters:${t ?? 'x'}`;

interface SavedFilters {
  warehouseFilter?: string;
  state?: StockState;
  categoryFilter?: string;
  brandFilter?: string;
  costFilter?: 'all' | 'with' | 'without';
  search?: string;
  pageSize?: number;
  sort?: { id: string; dir: 'asc' | 'desc' } | null;
}

/**
 * Column id → the API's sort token. Only these five can be ordered across the
 * WHOLE list; a column absent from this map is not offered as a sort at all
 * (see the columns below) rather than sorting the visible page and lying.
 */
const SERVER_SORTS = new Set(['item', 'warehouse', 'quantity', 'value', 'updatedAt']);

// Hidden until asked for — the default view is the one a stock count needs.
const DEFAULT_HIDDEN_COLS = new Set(['barcode', 'category', 'brand', 'value']);
const LOCKED_COLS = new Set(['item', 'actions']);

const PAGE_SIZES = [25, 50, 100, 250];

const STATES: readonly StockState[] = ['all', 'inStock', 'outOfStock', 'negative', 'low'];

/**
 * Filters named in the URL — `?state=low&warehouseId=…` — as the stock report's
 * "see them" links send. When any is present they REPLACE the remembered
 * filters rather than adding to them: someone who clicked "48 negative lines"
 * must see exactly those 48, not those among whatever they filtered by last.
 */
function filtersFromUrl(q: URLSearchParams): SavedFilters | null {
  const state = q.get('state');
  const hasCost = q.get('hasCost');
  const out: SavedFilters = {
    warehouseFilter: q.get('warehouseId') ?? undefined,
    categoryFilter: q.get('categoryId') ?? undefined,
    brandFilter: q.get('brandId') ?? undefined,
    state: state && (STATES as readonly string[]).includes(state) ? (state as StockState) : undefined,
    costFilter: hasCost === 'true' ? 'with' : hasCost === 'false' ? 'without' : undefined,
  };
  const any = Object.values(out).some((v) => v !== undefined);
  return any ? { ...out, search: '' } : null;
}

/** Σ quantity and Σ quantity × cost per currency over the rows in hand. */
function pageTotals(rows: StockLevel[]): { quantity: number; values: StockValueByCurrency[] } {
  let quantity = 0;
  const byCcy = new Map<string, StockValueByCurrency>();
  for (const l of rows) {
    const q = Number(l.quantity);
    quantity += q;
    if (!l.avgCostCurrencyCode) continue;
    const v = byCcy.get(l.avgCostCurrencyCode) ?? { currencyCode: l.avgCostCurrencyCode, value: '0', quantity: '0', lines: 0 };
    v.value = String(Number(v.value) + q * Number(l.avgCost));
    v.quantity = String(Number(v.quantity) + q);
    v.lines += 1;
    byCcy.set(l.avgCostCurrencyCode, v);
  }
  return { quantity, values: [...byCcy.values()].sort((a, b) => Number(b.value) - Number(a.value)) };
}
/** Below this, a line is "running low" rather than simply out. */
const LOW_THRESHOLD = '5';

export function StockPage(): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const canManageWarehouses = usePermission('warehouses.write');
  const canAdjust = usePermission('stock.adjust');
  const canImport = usePermission('items.write') && canAdjust;
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const tenantName = useAuthStore((s) => s.tenant?.name ?? '');
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const dir = useLayoutDirection();
  const [searchParams] = useSearchParams();
  const saved = useMemo(
    () => filtersFromUrl(searchParams)
      ?? loadViewPrefs<SavedFilters>(filtersKey(tenantId, userId), {}, legacyFiltersKey(tenantId)),
    // Read once on arrival: after that the page owns its filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, userId],
  );

  const [levels, setLevels] = useState<StockLevel[] | null>(null);
  const [total, setTotal] = useState(0);
  // Totals over EVERY line the filters select — the table holds one page.
  const [summary, setSummary] = useState<StockSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustInitial, setAdjustInitial] = useState<{ itemId: string; warehouseId: string } | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filters. All server-side: a catalogue of a few thousand products produces a
  // level per warehouse, so the browser never holds the whole set.
  const [search, setSearch] = useState(saved.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(saved.search ?? '');
  const [warehouseFilter, setWarehouseFilter] = useState(saved.warehouseFilter ?? 'all');
  const [state, setState] = useState<StockState>(saved.state ?? 'all');
  const [categoryFilter, setCategoryFilter] = useState(saved.categoryFilter ?? 'all');
  const [brandFilter, setBrandFilter] = useState(saved.brandFilter ?? 'all');
  const [costFilter, setCostFilter] = useState<'all' | 'with' | 'without'>(saved.costFilter ?? 'all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(saved.pageSize ?? 50);
  // Sorting is the SERVER's job here: this table holds one page of a list that
  // routinely runs to thousands of rows, so ordering the page in the browser
  // would rank 250 rows and present it as the whole catalogue — "sort by
  // quantity" would then show the biggest numbers ON THIS PAGE and hide the
  // bigger ones on the next nineteen.
  const [sort, setSort] = useState<{ id: string; dir: 'asc' | 'desc' } | null>(saved.sort ?? null);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [colVis, setColVis] = useState<Record<string, boolean>>(
    () => loadViewPrefs<Record<string, boolean>>(colsKey(tenantId, userId), {}, legacyColsKey(tenantId)),
  );
  const isColVisible = (id: string): boolean => colVis[id] ?? !DEFAULT_HIDDEN_COLS.has(id);

  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(h);
  }, [search]);

  useEffect(() => {
    saveViewPrefs(filtersKey(tenantId, userId), {
      warehouseFilter, state, categoryFilter, brandFilter, costFilter, search, pageSize, sort,
    });
  }, [tenantId, userId, warehouseFilter, state, categoryFilter, brandFilter, costFilter, search, pageSize, sort]);
  useEffect(() => { saveViewPrefs(colsKey(tenantId, userId), colVis); }, [tenantId, userId, colVis]);

  // Any filter change puts us back on page 1 — page 7 of the old result set is
  // meaningless against the new one.
  useEffect(() => {
    setPage(0);
  }, [warehouseFilter, state, categoryFilter, brandFilter, costFilter, debouncedSearch, pageSize]);

  useEffect(() => {
    void (async () => {
      const [w, c, b] = await Promise.all([
        api().get<{ items: Warehouse[] }>('/warehouses'),
        api().get<{ items: Category[] }>('/categories').catch(() => ({ data: { items: [] } })),
        api().get<{ items: Brand[] }>('/brands').catch(() => ({ data: { items: [] } })),
      ]);
      setWarehouses(w.data.items);
      setCategories(c.data.items);
      setBrands(b.data.items);
    })();
  }, []);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { limit: pageSize, offset: page * pageSize };
    if (warehouseFilter !== 'all') p.warehouseId = warehouseFilter;
    if (categoryFilter !== 'all') p.categoryId = categoryFilter;
    if (brandFilter !== 'all') p.brandId = brandFilter;
    if (state !== 'all') { p.state = state; if (state === 'low') p.lowThreshold = LOW_THRESHOLD; }
    if (costFilter !== 'all') p.hasCost = costFilter === 'with' ? 'true' : 'false';
    if (debouncedSearch.trim()) p.q = debouncedSearch.trim();
    if (sort && SERVER_SORTS.has(sort.id)) { p.sort = sort.id; p.dir = sort.dir; }
    return p;
  }, [pageSize, page, warehouseFilter, categoryFilter, brandFilter, state, costFilter, debouncedSearch, sort]);

  // The same filters without paging or order: the totals do not change when the
  // page does, so turning a page must not re-add up thousands of lines.
  const summaryParams = useMemo(() => {
    const { limit: _l, offset: _o, sort: _s, dir: _d, ...rest } = params;
    return rest;
  }, [params]);

  const reloadSummary = async (): Promise<void> => {
    try {
      const r = await api().get<StockSummary>('/stock/levels/summary', { params: summaryParams });
      setSummary(r.data);
    } catch {
      setSummary(null); // the list still works; only the totals are missing
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reloadSummary(); }, [summaryParams]);

  const reload = async (): Promise<void> => {
    setLoading(true);
    try {
      const r = await api().get<{ items: StockLevel[]; total: number }>('/stock/levels', { params });
      setLevels(r.data.items);
      setTotal(r.data.total);
    } finally {
      setLoading(false);
    }
  };
  // `params` is the memoized filter set — that IS the dependency. `reload` closes
  // over it and would be a new function every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [params]);

  /** After a change to stock: the page AND its totals. */
  const reloadAll = async (): Promise<void> => {
    await Promise.all([reload(), reloadSummary()]);
  };

  /**
   * Every line the filters select — not the page on screen — in the table's
   * order, as a styled .xlsx. One request with no limit (the summary already
   * reads the whole set the same way), so nothing can fall between pages.
   */
  const exportExcel = async (): Promise<void> => {
    setExporting(true);
    try {
      const { limit: _l, offset: _o, ...filters } = params;
      const r = await api().get<{ items: StockLevel[]; total: number }>('/stock/levels', { params: filters });
      const rows = r.data.items;
      const locale = i18n.language;
      const n = (v: number): string => formatNumber(v, { locale });
      const wh = warehouseFilter === 'all' ? null : warehouses.find((w) => w.id === warehouseFilter) ?? null;
      const cat = categoryFilter === 'all' ? null : categories.find((c) => c.id === categoryFilter) ?? null;
      const brand = brandFilter === 'all' ? null : brands.find((b) => b.id === brandFilter) ?? null;
      const filterLabels = [
        wh && `${t('stock.columns.warehouse')}: ${wh.name}`,
        state !== 'all' && t(`stock.state.${state}`),
        cat && `${t('stock.columns.category')}: ${cat.name}`,
        brand && `${t('stock.columns.brand')}: ${brand.title}`,
        costFilter !== 'all' && t(`stock.cost.${costFilter}`),
        debouncedSearch.trim() && `“${debouncedSearch.trim()}”`,
      ].filter((x): x is string => !!x);
      const sums = pageTotals(rows);
      const blank = (cells: Partial<Record<number, XlsxValue>>): XlsxValue[] =>
        Array.from({ length: 10 }, (_, i) => cells[i] ?? null);
      await downloadXlsx({
        fileName: `stock-${wh ? `${wh.code.replace(/[^\w-]+/g, '_')}-` : ''}${localDateStamp()}.xlsx`,
        sheetName: t('stock.title'),
        title: [tenantName, t('stock.title'), wh?.name].filter(Boolean).join(' — '),
        info: [
          tc('excel.exportedAt', { date: formatDateTime(new Date(), { locale }), n: n(rows.length) }),
          filterLabels.length ? tc('excel.filters', { list: filterLabels.join(' · ') }) : '',
        ],
        rtl: dir === 'rtl',
        columns: [
          { header: t('stock.columns.warehouse') },
          { header: tc('excel.code'), kind: 'code' },
          { header: tc('excel.product') },
          { header: t('stock.columns.barcode'), kind: 'code' },
          { header: t('stock.columns.category') },
          { header: t('stock.columns.brand') },
          { header: t('stock.columns.quantity'), kind: 'number' },
          { header: t('stock.columns.avgCost'), kind: 'money', unitPrice: true },
          { header: tc('excel.currency') },
          { header: t('stock.columns.value'), kind: 'money' },
        ],
        rows: rows.map((l) => {
          const q = Number(l.quantity);
          const ccy = l.avgCostCurrencyCode;
          return [
            l.warehouseName,
            l.itemCode,
            l.itemName,
            l.itemBarcode,
            l.categoryName,
            l.brandName,
            q,
            ccy ? { money: Number(l.avgCost), currency: ccy } : null,
            ccy,
            ccy ? { money: q * Number(l.avgCost), currency: ccy } : null,
          ];
        }),
        // Value per currency, each on its own row — never one sum across them.
        totals: [
          blank({ 0: tc('excel.totalLines', { n: n(rows.length) }), 6: sums.quantity }),
          ...sums.values.map((v) => blank({
            0: tc('excel.valueIn', { currency: v.currencyCode }),
            8: v.currencyCode,
            9: { money: Number(v.value), currency: v.currencyCode },
          })),
        ],
      });
    } catch {
      toast.error(tc('excel.failed'));
    } finally {
      setExporting(false);
    }
  };

  const openAdjust = (initial: { itemId: string; warehouseId: string } | null): void => {
    setAdjustInitial(initial);
    setAdjustOpen(true);
  };

  // Drop the whole item × warehouse record. Anything still on hand is written
  // off server-side (a negative adjustment movement), so this is destructive —
  // always confirm, and spell out the quantity that is about to disappear.
  const onRemove = async (l: StockLevel): Promise<void> => {
    const quantity = trimMoney(l.quantity);
    const empty = Number(l.quantity) === 0;
    const confirmed = await confirmDialog({
      title: t('stock.remove.title'),
      message: empty
        ? t('stock.remove.confirmEmpty', { item: l.itemName, warehouse: l.warehouseName })
        : t('stock.remove.confirm', { item: l.itemName, warehouse: l.warehouseName, quantity }),
      tone: 'danger',
      confirmLabel: t('stock.remove.action'),
    });
    if (!confirmed) return;
    try {
      await api().delete(`/stock/levels/${l.itemId}/${l.warehouseId}`);
      toast.success(t('stock.remove.done', { item: l.itemName }));
      await reloadAll();
    } catch (e) {
      // Someone else already removed it — refresh so the row goes away anyway.
      if ((e as { response?: { status?: number } }).response?.status === 404) {
        toast.error(t('stock.remove.gone'));
        await reloadAll();
        return;
      }
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<StockLevel>[] = [
    {
      id: 'warehouse',
      header: t('stock.columns.warehouse'),
      sortValue: (l) => l.warehouseCode,
      cell: (l) => (
        <>
          <span className="font-mono text-xs">{l.warehouseCode}</span> · {l.warehouseName}
        </>
      ),
    },
    {
      id: 'item',
      header: t('stock.columns.item'),
      sortValue: (l) => l.itemCode,
      cell: (l) => (
        <Link to={`/inventory/items/${l.itemId}`} className="hover:text-brand">
          <span className="font-mono text-xs">{l.itemCode}</span> · {l.itemName}
        </Link>
      ),
    },
    {
      id: 'barcode',
      header: t('stock.columns.barcode', { defaultValue: 'Barcode' }),
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (l) => l.itemBarcode ?? '—',
    },
    {
      id: 'category',
      header: t('stock.columns.category', { defaultValue: 'Category' }),
      cellClassName: 'text-fg-muted',
      cell: (l) => l.categoryName ?? '—',
    },
    {
      id: 'brand',
      header: t('stock.columns.brand', { defaultValue: 'Brand' }),
      cellClassName: 'text-fg-muted',
      cell: (l) => l.brandName ?? '—',
    },
    {
      id: 'quantity',
      header: t('stock.columns.quantity'),
      align: 'end',
      sortValue: (l) => Number(l.quantity),
      cellClassName: 'font-mono tabular-nums',
      cell: (l) => (
        <span className={cn(Number(l.quantity) < 0 && 'text-danger', Number(l.quantity) === 0 && 'text-fg-subtle')}>
          {trimMoney(l.quantity)}
        </span>
      ),
    },
    {
      id: 'avgCost',
      header: t('stock.columns.avgCost'),
      align: 'end',
      // Not sortable on purpose: the server ranks the other five columns across
      // the whole list, and cost is not one of them — offering it here would
      // only rank the page. Sort by "Stock value" (quantity × cost), which is.

      cellClassName: 'font-mono tabular-nums',
      cell: (l) =>
        l.avgCostCurrencyCode
          ? formatMoney(l.avgCost, {
              locale: i18n.language,
              currency: l.avgCostCurrencyCode,
            })
          : '—',
    },
    {
      id: 'value',
      header: t('stock.columns.value', { defaultValue: 'Stock value' }),
      align: 'end',
      sortValue: (l) => (l.avgCostCurrencyCode ? Number(l.quantity) * Number(l.avgCost) : null),
      cellClassName: 'font-mono tabular-nums',
      // Quantity × average cost, in the cost's OWN currency. Never summed across
      // the page: two rows can be valued in different currencies and nothing here
      // converts.
      cell: (l) =>
        l.avgCostCurrencyCode
          ? formatMoney(String(Number(l.quantity) * Number(l.avgCost)), {
              locale: i18n.language,
              currency: l.avgCostCurrencyCode,
            })
          : '—',
    },
    {
      id: 'updatedAt',
      header: t('stock.columns.updatedAt'),
      sortValue: (l) => l.updatedAt,
      cellClassName: 'text-fg-muted',
      cell: (l) => formatDateTime(l.updatedAt, { locale: i18n.language }),
    },
    ...(canAdjust
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-24',
            cell: (l: StockLevel) => (
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openAdjust({ itemId: l.itemId, warehouseId: l.warehouseId })}
                  aria-label={`${t('stock.adjust.action')} ${l.itemCode}`}
                  title={t('stock.adjust.action')}
                >
                  <ArrowUpDown className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger hover:text-danger"
                  onClick={() => void onRemove(l)}
                  aria-label={`${t('stock.remove.action')} ${l.itemCode}`}
                  title={t('stock.remove.action')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const hideableColumns = columns.filter((c) => !LOCKED_COLS.has(c.id));
  const visibleColumns = columns.filter((c) => LOCKED_COLS.has(c.id) || isColVisible(c.id));

  const selectCls = 'h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted';
  const comboOptions = (rows: Array<{ id: string; label: string }>, allLabel: string): ComboboxOption[] => [
    { value: 'all', label: allLabel },
    ...rows.map((r) => ({ value: r.id, label: r.label })),
  ];

  const anyFilter =
    warehouseFilter !== 'all' || state !== 'all' || categoryFilter !== 'all' ||
    brandFilter !== 'all' || costFilter !== 'all' || debouncedSearch.trim() !== '';

  const clearFilters = (): void => {
    setWarehouseFilter('all');
    setState('all');
    setCategoryFilter('all');
    setBrandFilter('all');
    setCostFilter('all');
    setSearch('');
  };

  const header = (
    <header className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('stock.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('stock.description')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {canImport && (
          <Button variant="secondary" onClick={() => navigate('/inventory/stock/import')}>
            <Upload className="me-1.5 h-4 w-4" aria-hidden />
            {t('stock.import.button', { defaultValue: 'Import from Excel' })}
          </Button>
        )}
        {canAdjust && <Button onClick={() => openAdjust(null)}>{t('stock.adjust.new')}</Button>}
      </div>
    </header>
  );

  if (!levels) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      </div>
    );
  }

  const nf = (n: number | string): string => formatNumber(Number(n), { locale: i18n.language });
  const moneyLines = (values: StockValueByCurrency[]): JSX.Element | string =>
    values.length === 0 ? '—' : (
      <span className="inline-flex flex-col items-end gap-0.5">
        {values.map((v) => (
          <span key={v.currencyCode}>{formatMoney(v.value, { locale: i18n.language, currency: v.currencyCode })}</span>
        ))}
      </span>
    );
  // The label sits under whichever column comes first — the user may have
  // hidden the warehouse column.
  const labelCol = visibleColumns[0]?.id ?? 'item';
  const pageSum = pageTotals(levels);
  const footerRows: DataTableFooterRow[] = [];
  if (total > levels.length) {
    footerRows.push({
      key: 'page',
      className: 'text-xs text-fg-muted',
      cells: {
        [labelCol]: t('stock.totals.page', { defaultValue: 'This page · lines: {{n}}', n: nf(levels.length) }),
        quantity: <span className="font-mono tabular-nums">{nf(pageSum.quantity)}</span>,
        value: <span className="font-mono tabular-nums">{moneyLines(pageSum.values)}</span>,
      },
    });
  }
  if (summary) {
    footerRows.push({
      key: 'total',
      className: 'font-semibold',
      cells: {
        [labelCol]: t('stock.totals.all', {
          defaultValue: 'Total · lines: {{lines}} · products: {{items}}',
          lines: nf(summary.lines),
          items: nf(summary.items),
        }),
        quantity: <span className="font-mono tabular-nums">{nf(summary.quantity)}</span>,
        value: <span className="font-mono tabular-nums">{moneyLines(summary.values)}</span>,
      },
    });
  }

  /** A tile in the strip; `onClick` makes it a one-click filter. */
  const tile = (
    key: string,
    label: string,
    value: string,
    opts: { hint?: string; icon?: JSX.Element; tone?: 'danger' | 'warning' | 'muted'; active?: boolean; onClick?: () => void } = {},
  ): JSX.Element => {
    const body = (
      <>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
          {opts.icon}
          {label}
        </span>
        <span
          className={cn(
            'font-mono text-base font-semibold tabular-nums',
            opts.tone === 'danger' && 'text-danger',
            opts.tone === 'warning' && 'text-warning',
          )}
        >
          {value}
        </span>
        {opts.hint && <span className="text-[11px] text-fg-subtle">{opts.hint}</span>}
      </>
    );
    const cls = cn(
      'flex min-w-[9rem] flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-start',
      opts.active ? 'border-brand bg-brand-subtle' : 'border-border bg-bg',
    );
    return opts.onClick ? (
      <button key={key} type="button" onClick={opts.onClick} aria-pressed={opts.active} className={cn(cls, 'transition-colors hover:border-brand')}>
        {body}
      </button>
    ) : (
      <div key={key} className={cls}>{body}</div>
    );
  };
  /** Toggle a quick filter: a second click puts the list back. */
  const quickState = (next: StockState): void => setState((cur) => (cur === next ? 'all' : next));
  const strip = summary && (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('stock.totals.title', { defaultValue: 'Totals for this list' })}>
      {tile('items', t('stock.totals.items', { defaultValue: 'Products' }), nf(summary.items), {
        hint: t('stock.totals.linesHint', { defaultValue: 'Lines (product × warehouse): {{n}}', n: nf(summary.lines) }),
      })}
      {tile('qty', t('stock.totals.quantity', { defaultValue: 'Units on hand' }), nf(summary.quantity))}
      {tile(
        'value',
        t('stock.totals.value', { defaultValue: 'Stock value' }),
        summary.values.length
          ? summary.values.map((v) => formatMoney(v.value, { locale: i18n.language, currency: v.currencyCode })).join(' · ')
          : '—',
        { hint: t('stock.totals.valueHint', { defaultValue: 'quantity × average cost, per currency' }) },
      )}
      {tile('out', t('stock.totals.out', { defaultValue: 'Out of stock' }), nf(summary.outOfStockLines), {
        icon: <PackageX className="h-3.5 w-3.5" aria-hidden />,
        tone: summary.outOfStockLines ? 'warning' : undefined,
        active: state === 'outOfStock',
        onClick: () => quickState('outOfStock'),
      })}
      {tile('low', t('stock.totals.low', { defaultValue: 'Running low (≤ {{n}})', n: nf(summary.lowThreshold) }), nf(summary.lowLines), {
        icon: <TrendingDown className="h-3.5 w-3.5" aria-hidden />,
        tone: summary.lowLines ? 'warning' : undefined,
        active: state === 'low',
        onClick: () => quickState('low'),
      })}
      {tile('neg', t('stock.totals.negative', { defaultValue: 'Negative' }), nf(summary.negativeLines), {
        icon: <AlertTriangle className="h-3.5 w-3.5" aria-hidden />,
        tone: summary.negativeLines ? 'danger' : undefined,
        active: state === 'negative',
        onClick: () => quickState('negative'),
      })}
      {tile('nocost', t('stock.totals.noCost', { defaultValue: 'On hand, no cost' }), nf(summary.noCostLines), {
        icon: <CircleOff className="h-3.5 w-3.5" aria-hidden />,
        tone: summary.noCostLines ? 'warning' : undefined,
        hint: t('stock.totals.noCostHint', { defaultValue: 'Units not in the value: {{n}}', n: nf(summary.noCostQuantity) }),
        active: state === 'inStock' && costFilter === 'without',
        onClick: () => {
          const on = state === 'inStock' && costFilter === 'without';
          setState(on ? 'all' : 'inStock');
          setCostFilter(on ? 'all' : 'without');
        },
      })}
    </div>
  );

  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + levels.length);
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <div className="flex flex-col gap-4">
      {header}
      {levels.length === 0 && !anyFilter ? (
        <EmptyState
          icon={<Package className="h-8 w-8" aria-hidden />}
          title={t('stock.empty')}
          description={t('stock.emptyDescription')}
          action={
            canManageWarehouses ? (
              <Link to="/inventory/warehouses">
                <Button size="sm" variant="secondary">{t('stock.setupWarehouses')}</Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
        {strip}
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={visibleColumns}
            footerRows={footerRows}
            data={levels}
            rowKey={(l) => `${l.itemId}-${l.warehouseId}`}
            searchable={false}
            // Paging is SERVER-side (see the footer below) — the table only ever
            // holds the current page, so its own pager would lie about the total.
            paginated={false}
            // …and for the same reason so is sorting: passing these two makes the
            // header ask the server to re-rank all 4,831 rows, not the 250 here.
            sort={sort}
            onSortChange={(next) => { setSort(next); setPage(0); }}
            externallySorted
            toolbar={
              <div className="flex flex-wrap items-center gap-1.5">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('stock.searchPlaceholder', { defaultValue: 'Search item, code or barcode…' })}
                  aria-label={t('stock.searchPlaceholder', { defaultValue: 'Search item, code or barcode…' })}
                  className="h-8 w-60 rounded-full border border-border bg-bg px-3.5 text-xs text-fg outline-none transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/20"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="h-8 gap-1.5 rounded-full">
                      <Columns3 className="h-4 w-4" aria-hidden />
                      {t('stock.columnsMenu', { defaultValue: 'Columns' })}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-80 overflow-auto">
                    <DropdownMenuLabel>{t('stock.columnsMenu', { defaultValue: 'Columns' })}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {hideableColumns.map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        checked={isColVisible(col.id)}
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={(v) => setColVis((m) => ({ ...m, [col.id]: v }))}
                      >
                        {typeof col.header === 'string' && col.header ? col.header : col.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 gap-1.5 rounded-full"
                  onClick={() => void exportExcel()}
                  disabled={exporting || total === 0}
                >
                  <FileSpreadsheet className="h-4 w-4" aria-hidden />
                  {exporting ? tc('excel.exporting') : tc('excel.export')}
                </Button>
                {warehouses.length > 1 && (
                  <select
                    value={warehouseFilter}
                    onChange={(e) => setWarehouseFilter(e.target.value)}
                    aria-label={t('stock.columns.warehouse')}
                    className={selectCls}
                  >
                    <option value="all">{t('stock.allWarehouses', { defaultValue: 'All warehouses' })}</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                )}
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value as StockState)}
                  aria-label={t('stock.stateFilter', { defaultValue: 'Stock level' })}
                  className={selectCls}
                >
                  <option value="all">{t('stock.state.all', { defaultValue: 'Any quantity' })}</option>
                  <option value="inStock">{t('stock.state.inStock', { defaultValue: 'In stock' })}</option>
                  <option value="low">{t('stock.state.low', { defaultValue: 'Low (≤ 5)' })}</option>
                  <option value="outOfStock">{t('stock.state.outOfStock', { defaultValue: 'Out of stock' })}</option>
                  <option value="negative">{t('stock.state.negative', { defaultValue: 'Negative' })}</option>
                </select>
                {categories.length > 0 && (
                  <div className="w-44">
                    <Combobox
                      size="sm"
                      options={comboOptions(
                        categories.map((c) => ({ id: c.id, label: c.name })),
                        t('stock.allCategories', { defaultValue: 'All categories' }),
                      )}
                      value={categoryFilter}
                      onChange={(v) => setCategoryFilter(v ?? 'all')}
                      placeholder={t('stock.allCategories', { defaultValue: 'All categories' })}
                      ariaLabel={t('stock.allCategories', { defaultValue: 'All categories' })}
                    />
                  </div>
                )}
                {brands.length > 0 && (
                  <div className="w-44">
                    <Combobox
                      size="sm"
                      options={comboOptions(
                        brands.map((b) => ({ id: b.id, label: b.title })),
                        t('stock.allBrands', { defaultValue: 'All brands' }),
                      )}
                      value={brandFilter}
                      onChange={(v) => setBrandFilter(v ?? 'all')}
                      placeholder={t('stock.allBrands', { defaultValue: 'All brands' })}
                      ariaLabel={t('stock.allBrands', { defaultValue: 'All brands' })}
                    />
                  </div>
                )}
                <select
                  value={costFilter}
                  onChange={(e) => setCostFilter(e.target.value as typeof costFilter)}
                  aria-label={t('stock.columns.avgCost')}
                  className={selectCls}
                >
                  <option value="all">{t('stock.cost.all', { defaultValue: 'Any cost' })}</option>
                  <option value="with">{t('stock.cost.with', { defaultValue: 'Has cost' })}</option>
                  <option value="without">{t('stock.cost.without', { defaultValue: 'No cost' })}</option>
                </select>
                {anyFilter && (
                  <Button variant="ghost" size="sm" className="h-8 rounded-full" onClick={clearFilters}>
                    {t('stock.clearFilters', { defaultValue: 'Clear' })}
                  </Button>
                )}
              </div>
            }
          />
          {/* Server-side pager. The count is the FULL filtered total, not the page. */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-fg-muted">
            <div className="flex items-center gap-2">
              <span>
                {t('stock.showing', {
                  defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
                  from: formatNumber(from, { locale: i18n.language }),
                  to: formatNumber(to, { locale: i18n.language }),
                  total: formatNumber(total, { locale: i18n.language }),
                })}
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label={t('stock.perPage', { defaultValue: 'Rows per page' })}
                className="h-7 rounded-full border border-border bg-bg px-2 text-xs"
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label={t('stock.prevPage', { defaultValue: 'Previous page' })}
              >
                <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
              </Button>
              <span className="px-1 tabular-nums">{page + 1} / {lastPage + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= lastPage || loading}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                aria-label={t('stock.nextPage', { defaultValue: 'Next page' })}
              >
                <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
              </Button>
            </div>
          </div>
        </Card>
        </>
      )}
      {canAdjust && (
        <AdjustStockDialog
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          levels={levels}
          initial={adjustInitial}
          onDone={reloadAll}
        />
      )}
    </div>
  );
}
